import { UserType } from "@/constants";
import type { AdminUserType } from "@/hera-api/client/types.gen";
import { jurisdictionOptionLabel } from "@/lib/jurisdiction-option-label";
import { z } from "zod";
import { Button } from "../ui/button";
import { Card, CardContent, CardFooter } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { FormFieldWrapper } from "../ui/form-field-wrapper";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Select } from "../ui/select";
import { Switch } from "../ui/switch";
import { useAppForm } from "../ui/tanstack-form";

export const UserFormSchema = z.object({
  auth0_subject: z.string().nullable(),
  email: z.string().email("Invalid email address"),
  name: z.string().min(1, "Name is required"),
  user_type: z.enum(["government", "operator"]),
  jurisdiction_id: z.string().uuid("Invalid jurisdiction ID").nullable(),
  is_deleted: z.boolean(),
  is_main_contact: z.boolean(),
  is_remittance_contact: z.boolean(),
  is_admin: z.boolean(),
  is_sales: z.boolean(),
});

interface UserFormProps {
  userId: string | null;
  userData: {
    auth0_subject: string;
    email: string;
    name: string | null;
    user_type: AdminUserType;
    jurisdiction_id: string | null;
    is_deleted: boolean;
    is_main_contact: boolean;
    is_remittance_contact: boolean;
    is_admin: boolean;
    is_sales: boolean;
  } | null;
  currentUserType: UserType.GOVERNMENT | UserType.OPERATOR;
  /** Operator admins can create operators and set Admin and Sales. */
  canManageOperators?: boolean;
  jurisdictions: {
    id: string;
    name: string;
  }[];
  onSubmit: (data: z.infer<typeof UserFormSchema>) => Promise<void>;
  onCancel: () => void;
}

export function UserForm({
  userId,
  userData,
  currentUserType,
  canManageOperators = false,
  jurisdictions,
  onSubmit,
  onCancel,
}: UserFormProps) {
  const form = useAppForm({
    defaultValues: {
      auth0_subject: userData?.auth0_subject || null,
      email: userData?.email || "",
      name: userData?.name || "",
      user_type:
        userData?.user_type ||
        (currentUserType === UserType.GOVERNMENT || !canManageOperators
          ? "government"
          : "operator"),
      jurisdiction_id: userData?.jurisdiction_id || null,
      is_deleted: userData?.is_deleted || false,
      is_main_contact: userData?.is_main_contact || false,
      is_remittance_contact: userData?.is_remittance_contact || false,
      is_admin: userData?.is_admin || false,
      is_sales: userData?.is_sales || false,
    },
    validators: {
      onSubmit: UserFormSchema,
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value);
    },
  });

  // No need for useEffect - the form will be recreated when userData changes

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await form.handleSubmit();
  };

  if (
    currentUserType === UserType.GOVERNMENT &&
    userData?.user_type === "operator"
  ) {
    throw new Error("You are not authorized to edit operator users.");
  }

  return (
    <Card>
      <CardContent>
        <form.AppForm>
          <form.Subscribe selector={(state) => [state.values.user_type]}>
            {([userType]) => (
              <form className="space-y-4" onSubmit={handleSubmit}>
                <form.AppField
                  name="email"
                  validators={{
                    onChange: UserFormSchema.shape.email,
                  }}
                >
                  {(field) => (
                    <FormFieldWrapper field={field} label="Email">
                      <Input
                        id="email"
                        placeholder="Email"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                    </FormFieldWrapper>
                  )}
                </form.AppField>
                <form.AppField
                  name="name"
                  validators={{
                    onChange: UserFormSchema.shape.name,
                  }}
                >
                  {(field) => (
                    <FormFieldWrapper field={field} label="Name">
                      <Input
                        id="name"
                        placeholder="Name"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                    </FormFieldWrapper>
                  )}
                </form.AppField>
                {currentUserType === UserType.OPERATOR &&
                  canManageOperators &&
                  !userId && (
                    <form.AppField
                      name="user_type"
                      validators={{
                        onChange: UserFormSchema.shape.user_type,
                      }}
                    >
                      {(field) => (
                        <FormFieldWrapper field={field} label="User Type">
                          <RadioGroup
                            value={field.state.value}
                            onValueChange={(value) => {
                              field.handleChange(
                                value as "government" | "operator",
                              );
                            }}
                            className="flex flex-col space-y-2"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="operator" id="operator" />
                              <Label htmlFor="operator" className="font-normal">
                                Operator (Hera)
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="government"
                                id="government"
                              />
                              <Label
                                htmlFor="government"
                                className="font-normal"
                              >
                                Government
                              </Label>
                            </div>
                          </RadioGroup>
                        </FormFieldWrapper>
                      )}
                    </form.AppField>
                  )}
                {currentUserType === UserType.OPERATOR &&
                  userType === "government" &&
                  !userId && (
                    <form.AppField
                      name="jurisdiction_id"
                      validators={{
                        onChange: UserFormSchema.shape.jurisdiction_id,
                      }}
                    >
                      {(field) => (
                        <FormFieldWrapper field={field} label="Jurisdiction">
                          <Select
                            options={jurisdictions.map((jurisdiction) => ({
                              value: jurisdiction.id,
                              label: jurisdictionOptionLabel(jurisdiction),
                            }))}
                            value={field.state.value ? [field.state.value] : []}
                            onValueChange={(values) => {
                              field.handleChange(values[0] || null);
                            }}
                          />
                        </FormFieldWrapper>
                      )}
                    </form.AppField>
                  )}
                {userType === "government" && (
                  <>
                    <form.AppField
                      name="is_main_contact"
                      validators={{
                        onChange: UserFormSchema.shape.is_main_contact,
                      }}
                    >
                      {(field) => (
                        <FormFieldWrapper field={field}>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="is_main_contact"
                              checked={field.state.value}
                              onCheckedChange={(checked) =>
                                field.handleChange(checked === true)
                              }
                            />
                            <Label
                              htmlFor="is_main_contact"
                              className="font-normal"
                            >
                              Main Contact
                            </Label>
                          </div>
                        </FormFieldWrapper>
                      )}
                    </form.AppField>
                    <form.AppField
                      name="is_remittance_contact"
                      validators={{
                        onChange: UserFormSchema.shape.is_remittance_contact,
                      }}
                    >
                      {(field) => (
                        <FormFieldWrapper field={field}>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="is_remittance_contact"
                              checked={field.state.value}
                              onCheckedChange={(checked) =>
                                field.handleChange(checked === true)
                              }
                            />
                            <Label
                              htmlFor="is_remittance_contact"
                              className="font-normal"
                            >
                              Remittance Contact
                            </Label>
                          </div>
                        </FormFieldWrapper>
                      )}
                    </form.AppField>
                  </>
                )}
                {userType === "operator" && canManageOperators && (
                  <>
                    <form.AppField
                      name="is_admin"
                      validators={{
                        onChange: UserFormSchema.shape.is_admin,
                      }}
                    >
                      {(field) => (
                        <FormFieldWrapper field={field}>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="is_admin"
                              checked={field.state.value}
                              onCheckedChange={(checked) =>
                                field.handleChange(checked === true)
                              }
                            />
                            <Label htmlFor="is_admin" className="font-normal">
                              Admin
                            </Label>
                          </div>
                        </FormFieldWrapper>
                      )}
                    </form.AppField>
                    <form.AppField
                      name="is_sales"
                      validators={{
                        onChange: UserFormSchema.shape.is_sales,
                      }}
                    >
                      {(field) => (
                        <FormFieldWrapper field={field}>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="is_sales"
                              checked={field.state.value}
                              onCheckedChange={(checked) =>
                                field.handleChange(checked === true)
                              }
                            />
                            <Label htmlFor="is_sales" className="font-normal">
                              Sales
                            </Label>
                          </div>
                        </FormFieldWrapper>
                      )}
                    </form.AppField>
                  </>
                )}
                {!!userId && (
                  <form.AppField
                    name="is_deleted"
                    validators={{
                      onChange: UserFormSchema.shape.is_deleted,
                    }}
                  >
                    {(field) => (
                      <FormFieldWrapper field={field} label="Is Deleted">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="is_deleted"
                            checked={field.state.value}
                            onCheckedChange={field.handleChange}
                          />
                        </div>
                      </FormFieldWrapper>
                    )}
                  </form.AppField>
                )}
              </form>
            )}
          </form.Subscribe>
        </form.AppForm>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <form.Subscribe
          selector={(state) =>
            [
              state.canSubmit,
              state.isSubmitting,
              state.values.user_type,
              state.values.jurisdiction_id,
            ] as const
          }
        >
          {([canSubmit, isSubmitting, selectedUserType, jurisdictionId]) => (
            <Button
              className="disabled:opacity-20 disabled:cursor-not-allowed"
              onClick={handleSubmit}
              disabled={
                !canSubmit ||
                Boolean(isSubmitting) ||
                (!userId &&
                  currentUserType === UserType.OPERATOR &&
                  selectedUserType === "government" &&
                  !jurisdictionId)
              }
            >
              {isSubmitting
                ? "Saving..."
                : !userId
                  ? "Create User"
                  : "Save Changes"}
            </Button>
          )}
        </form.Subscribe>
      </CardFooter>
    </Card>
  );
}
